import { Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { FiDatabase, FiDroplet, FiLayers } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import { Alert, ConfirmDialog, ExportButton, FormActions, FormCheckbox, FormField, FormSelect, FormTextarea, PageHeader, StatCard, TableActions } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ExcelColumn } from '../utils/exportToExcel';
import { AgingProcess } from './Aging';
import { FermentationProcess } from './Fermentation';

/** Shared column widths so Barrel/Tank (and other type) tables stay aligned under table-fixed. */
const VESSEL_TABLE_COLGROUP = (
  <colgroup>
    <col className="w-[10%]" />
    <col className="w-[8%]" />
    <col className="w-[10%]" />
    <col className="w-[12%]" />
    <col className="w-[7%]" />
    <col className="w-[12%]" />
    <col className="w-[10%]" />
    <col className="w-[18%]" />
    <col className="w-[13%]" />
  </colgroup>
);

// Interface for the vessel data from the server
interface Vessel {
  id: number;
  vesselId: string;
  vesselType: string;
  material: string;
  capacity: number;
  capacityUnit: string;
  usageType: string;
  status: string;
  notes?: string;
  kosher?: boolean;
  age?: number;
  manufacture?: string;
  previousOwner?: string;
  location?: string;
  vintage?: number;
  toast?: string;
  cooperage?: string;
}

// Interface for the form values (using snake_case for legacy reasons)
interface VesselFormValues {
  vessel_id: string;
  type: string;
  material: string;
  material_other?: string;
  capacity: number;
  capacity_unit: string;
  usage_type: string;
  status: string;
  notes?: string;
  kosher: boolean;
  age?: number;
  manufacture?: string;
  previous_owner?: string;
}

const VesselSchema = Yup.object().shape({
  vessel_id: Yup.string().required('Vessel ID is required'),
  type: Yup.string().required('Type is required'),
  capacity: Yup.number().required('Capacity is required').positive('Capacity must be positive'),
  capacity_unit: Yup.string().required('Capacity unit is required'),
  material: Yup.string().required('Material is required'),
  material_other: Yup.string()
    .nullable()
    .when('material', {
      is: 'Other',
      then: (schema) => schema.required('Please specify the material when "Other" is selected'),
      otherwise: (schema) => schema.nullable()
    }),
  usage_type: Yup.string().required('Usage type is required'),
  status: Yup.string().required('Status is required'),
  notes: Yup.string(),
  kosher: Yup.boolean(),
  age: Yup.number().integer('Age must be an integer').min(0, 'Age cannot be negative').nullable(),
  manufacture: Yup.string().nullable(),
  previous_owner: Yup.string().nullable()
});

const vesselTypes = [
  { value: 'Tank', label: 'Tank' },
  { value: 'Barrel', label: 'Barrel' },
  { value: 'Tote', label: 'Tote' },
  { value: 'Bin', label: 'Bin' },
  { value: 'Other', label: 'Other' }
];

const capacityUnits = [
  { value: 'L', label: 'Liters' },
  { value: 'gal', label: 'Gallons' },
  { value: 'hL', label: 'Hectoliters' }
];

const vesselMaterials = [
  { value: 'Stainless Steel', label: 'Stainless Steel' },
  { value: 'French Oak', label: 'French Oak' },
  { value: 'American Oak', label: 'American Oak' },
  { value: 'Hungarian Oak', label: 'Hungarian Oak' },
  { value: 'Plastic', label: 'Plastic' },
  { value: 'Concrete', label: 'Concrete' },
  { value: 'Other', label: 'Other' }
];

const usageTypeOptions = [
  { value: 'Aging', label: 'Aging' },
  { value: 'Fermentation', label: 'Fermentation' },
  { value: 'Both', label: 'Both' }
];

const statusOptions = [
  { value: 'Empty', label: 'Empty' },
  { value: 'In Use', label: 'In Use' },
  { value: 'Out of Service', label: 'Out of Service' }
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const toastLevels = [
  { value: 'None', label: 'None' },
  { value: 'Light', label: 'Light' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Medium Plus', label: 'Medium Plus' },
  { value: 'Heavy', label: 'Heavy' }
];

// Helper function to convert vessel to form values
const vesselToFormValues = (vessel: Vessel | null): VesselFormValues => {
  if (!vessel) {
    return {
      vessel_id: '',
      type: '',
      material: '',
      material_other: '',
      capacity: 0,
      capacity_unit: 'L',
      usage_type: 'Aging',
      status: 'Empty',
      notes: '',
      kosher: false,
      age: undefined,
      manufacture: '',
      previous_owner: ''
    };
  }

  return {
    vessel_id: vessel.vesselId,
    type: vessel.vesselType,
    material: vessel.material,
    material_other: (vessel as any).material_other || '',
    capacity: vessel.capacity,
    capacity_unit: vessel.capacityUnit,
    usage_type: vessel.usageType,
    status: vessel.status,
    notes: vessel.notes || '',
    kosher: Boolean(vessel.kosher),
    age: vessel.age,
    manufacture: vessel.manufacture || '',
    previous_owner: vessel.previousOwner || ''
  };
};

const Vessels: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';
  const location = useLocation();
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [activeFermentations, setActiveFermentations] = useState<FermentationProcess[]>([]);
  const [activeAgingProcesses, setActiveAgingProcesses] = useState<AgingProcess[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  /** API errors from create/update — shown inside the vessel modal */
  const [formError, setFormError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingVessel, setEditingVessel] = useState<Vessel | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; vesselId?: number }>({ open: false });
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [usageTypeFilter, setUsageTypeFilter] = useState('all');

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [vesselsRes, fermentationsRes, agingRes] = await Promise.all([
          api.get('/api/vessel'),
          api.get('/api/fermentation'),
          api.get('/api/aging')
        ]);

        console.log('Fetched vessels data:', vesselsRes.data);
        setVessels(vesselsRes.data);

        const activeFerm = fermentationsRes.data.filter((f: FermentationProcess) => f.status === 'Active');
        const activeAging = agingRes.data.filter((a: AgingProcess) => a.status === 'Active');
        setActiveFermentations(activeFerm);
        setActiveAgingProcesses(activeAging);
        console.log('Active Fermentations:', activeFerm);
        console.log('Active Aging Processes:', activeAging);

        const params = new URLSearchParams(location.search);
        const vesselId = params.get('id');

        if (vesselId) {
          const vessel = vesselsRes.data.find((v: Vessel) => v.id === parseInt(vesselId));
          if (vessel) {
            setFormError(null);
            setEditingVessel(vessel);
            setIsModalOpen(true);
          }
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [location]);

  const openModal = (vessel: Vessel | null = null) => {
    setFormError(null);
    setEditingVessel(vessel);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingVessel(null);
    setFormError(null);
  };

  const toggleExpand = (id: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin) return;
    setConfirmDelete({ open: true, vesselId: id });
  };

  const confirmDeleteVessel = async () => {
    if (!confirmDelete.vesselId) return;
    try {
      await api.delete(`/api/vessel/${confirmDelete.vesselId}`);
      setVessels(vessels.filter(v => v.id !== confirmDelete.vesselId));
      setSuccessMessage('Vessel deleted successfully');
      setConfirmDelete({ open: false });
    } catch (err: any) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.dependencies) {
        const d = data.dependencies || {};
        const parts: string[] = [];
        if (d.fermentations) parts.push(`${d.fermentations} fermentations`);
        if (d.aging) parts.push(`${d.aging} aging records`);
        setError(`Cannot delete vessel. Move or delete related ${parts.join(', ')} first.`);
      } else {
        setError(err);
      }
      setConfirmDelete({ open: false });
    }
  };

  const handleSubmit = async (values: VesselFormValues, { resetForm, setSubmitting }: any) => {
    try {
      setFormError(null);
      const serverData = {
        vesselId: values.vessel_id,
        vesselType: values.type,
        material: values.material,
        material_other: values.material_other,
        capacity: values.capacity,
        capacityUnit: values.capacity_unit,
        usageType: values.usage_type,
        status: values.status,
        notes: values.notes,
        kosher: values.kosher,
        age: values.age,
        manufacture: values.manufacture,
        previousOwner: values.previous_owner
      };

      if (editingVessel) {
        const response = await api.put(`/api/vessel/${editingVessel.id}`, serverData);
        setVessels(vessels.map(v => (v.id === editingVessel.id ? response.data : v)));
        setSuccessMessage('Vessel updated successfully!');
      } else {
        const response = await api.post('/api/vessel', serverData);
        setVessels([...vessels, response.data]);
        setSuccessMessage('Vessel created successfully!');
      }
      resetForm();
      closeModal();

      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving vessel:', err);
      // Keep modal open and show the error inside the popup
      setFormError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Map active fermentations by vessel ID for quick lookup
  const fermentingVesselMap = useMemo(() => {
    const map = new Map<number, FermentationProcess>();
    activeFermentations.forEach(f => map.set(f.vessel_id, f));
    return map;
  }, [activeFermentations]);

  const agingVesselMap = useMemo(() => {
    const map = new Map<number, AgingProcess>();
    activeAgingProcesses.forEach(a => map.set(a.vesselId, a));
    return map;
  }, [activeAgingProcesses]);

  if (loading) {
    return <LoadingSpinner />;
  }

  const filteredVessels = vessels.filter(vessel => {
    return (typeFilter === 'all' || vessel.vesselType === typeFilter) &&
      (statusFilter === 'all' || vessel.status === statusFilter) &&
      (usageTypeFilter === 'all' || vessel.usageType === usageTypeFilter);
  });

  const vesselsByType: { [key: string]: Vessel[] } = {};
  filteredVessels.forEach(vessel => {
    if (!vesselsByType[vessel.vesselType]) {
      vesselsByType[vessel.vesselType] = [];
    }
    vesselsByType[vessel.vesselType].push(vessel);
  });

  const uniqueTypes = Array.from(new Set(vessels.map(v => v.vesselType)));
  const uniqueStatuses = Array.from(new Set(vessels.map(v => v.status)));

  const totalCapacity = filteredVessels.reduce((sum, v) => {
    // DECIMAL from Sequelize may arrive as a string; coerce before arithmetic
    let capacityInLiters = Number(v.capacity);
    if (!Number.isFinite(capacityInLiters)) capacityInLiters = 0;
    if (v.capacityUnit === 'gal') capacityInLiters *= 3.785;
    if (v.capacityUnit === 'hL') capacityInLiters *= 100;
    return sum + capacityInLiters;
  }, 0);

  const inUseVessels = filteredVessels.filter(v => v.status === 'In Use').length;

  // Export columns definition for ExportButton
  const exportColumns: ExcelColumn<Vessel>[] = [
    { header: 'ID', accessor: (v) => v.vesselId },
    { header: 'Type', accessor: (v) => v.vesselType },
    {
      header: 'Capacity',
      accessor: (v) => `${v.capacity} ${v.capacityUnit}`,
    },
    {
      header: 'Material',
      accessor: (v) =>
        v.material === 'Other' && (v as any).material_other
          ? `Other (${(v as any).material_other})`
          : v.material,
    },
    {
      header: 'Kosher',
      accessor: (v) => (v.kosher ? 'Yes' : 'No'),
    },
    { header: 'Usage Type', accessor: (v) => v.usageType },
    { header: 'Status', accessor: (v) => v.status },
    { header: 'Location', accessor: (v) => v.location || '' },
  ];

  return (
    <div className="space-y-7">
      <PageHeader
        title="Vessel Management"
        subtitle="Track and manage tanks, barrels, and other vessels."
        actions={(
        <div className="flex gap-3">
          <ExportButton
            data={filteredVessels}
            columns={exportColumns}
            fileName="vessels"
            sheetName="Vessels"
          />
          <PermissionButton
            resource="vessel"
            action="create"
            onClick={() => openModal()}
          >
            Add Vessel
          </PermissionButton>
        </div>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <Alert
          type="success"
          message={successMessage}
          autoHide
          onClose={() => setSuccessMessage(null)}
          className="mt-4"
        />
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Total Vessels" value={filteredVessels.length} icon={FiDatabase({ size: 20 })} color="primary" />
        <StatCard title="Total Capacity" value={`${Math.round(totalCapacity).toLocaleString()} L`} icon={FiDroplet({ size: 20 })} color="info" />
        <StatCard title="In Use" value={`${inUseVessels} / ${filteredVessels.length}`} icon={FiLayers({ size: 20 })} color="success" />
      </div>

      <div className="mt-6 bg-white/85 backdrop-blur rounded-xl shadow border border-monday-border-light overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-monday-text">Filters</h3>
          <div className="mt-4 grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6">
            <div>
              <label htmlFor="typeFilter" className="form-label">
                Vessel Type
              </label>
              <select
                id="typeFilter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="mt-1 block w-full py-2 px-3 border border-monday-border rounded-xl shadow-xs focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm transition-all duration-150"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="usageTypeFilter" className="form-label">Usage Type</label>
              <select
                id="usageTypeFilter"
                value={usageTypeFilter}
                onChange={(e) => setUsageTypeFilter(e.target.value)}
                className="mt-1 block w-full py-2 px-3 border border-monday-border rounded-xl shadow-xs focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm transition-all duration-150"
              >
                <option value="all">All Usages</option>
                <option value="Aging">Aging</option>
                <option value="Fermentation">Fermentation</option>
                <option value="Both">Both</option>
              </select>
            </div>

            <div>
              <label htmlFor="statusFilter" className="form-label">
                Status
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="mt-1 block w-full py-2 px-3 border border-monday-border rounded-xl shadow-xs focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm transition-all duration-150"
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {Object.keys(vesselsByType).length === 0 ? (
          <div className="bg-white/95 rounded-xl shadow border border-monday-border-light overflow-hidden">
            <div className="px-4 py-5 sm:px-6 text-center text-monday-text-muted">
              No vessels match the current filters
            </div>
          </div>
        ) : (
          Object.entries(vesselsByType).map(([type, typedVessels]) => (
            <div key={type} className="mb-8">
              <h2 className="text-xl font-semibold text-monday-text mb-4">{type} Vessels</h2>
              <div className="bg-white/95 rounded-xl shadow border border-monday-border-light overflow-hidden">
                <table className="min-w-full w-full table-fixed divide-y divide-monday-border-light">
                  {VESSEL_TABLE_COLGROUP}
                  <thead className="bg-monday-header">
                    <tr>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        ID
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        Capacity
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        Material
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        Kosher
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        Usage Type
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        Current Use
                      </th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-monday-text-muted uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-monday-border-light">
                    {typedVessels.map((vessel) => (
                      <React.Fragment key={vessel.id}>
                        <tr className="group hover:bg-monday-hover transition-colors duration-150">
                          <td className="whitespace-nowrap px-3 py-4 truncate">
                            <span className="data-row-primary" title={vessel.vesselId}>{vessel.vesselId}</span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 truncate">
                            <span className="data-cell-muted" title={vessel.vesselType}>{vessel.vesselType}</span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 truncate">
                            <span className="data-cell-muted" title={`${vessel.capacity} ${vessel.capacityUnit}`}>
                              {vessel.capacity} {vessel.capacityUnit}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted truncate">
                            {(() => {
                              const materialLabel = vessel.material === 'Other' && (vessel as any).material_other
                                ? `Other (${(vessel as any).material_other})`
                                : vessel.material;
                              return <span title={materialLabel}>{materialLabel}</span>;
                            })()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {vessel.kosher ? 'Yes' : 'No'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted truncate">
                            <span title={vessel.usageType}>{vessel.usageType}</span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded
                            ${fermentingVesselMap.has(vessel.id) || agingVesselMap.has(vessel.id) ? 'bg-success-500 text-white' :
                                vessel.status === 'Out of Service' ? 'bg-red-100 text-red-800' :
                                  'bg-monday-status-neutral text-white'}`}>
                              {fermentingVesselMap.has(vessel.id) || agingVesselMap.has(vessel.id) ? 'In Use' : vessel.status}
                            </span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-monday-text-muted max-w-0 truncate">
                            {(() => {
                              // A vessel can only be used for either fermentation OR aging, not both
                              const fermentProcess = fermentingVesselMap.get(vessel.id);
                              const agingProcess = agingVesselMap.get(vessel.id);

                              // Check fermentation first (takes priority)
                              if (fermentProcess) {
                                const label = `Ferm: ${fermentProcess.wineBatch?.batch_name || 'Batch ' + fermentProcess.batch_id}`;
                                return (
                                  <Link
                                    to={`/fermentation?id=${fermentProcess.id}`}
                                    className="block truncate text-primary-500 hover:text-primary-600"
                                    title={label}
                                  >
                                    {label}
                                  </Link>
                                );
                              }

                              // If no fermentation, check for aging
                              if (agingProcess) {
                                const label = `Aging: ${agingProcess.batch?.batch_name || 'Batch ' + agingProcess.batch_id}`;
                                return (
                                  <Link
                                    to={`/aging?id=${agingProcess.id}`}
                                    className="block truncate text-primary-500 hover:text-primary-600"
                                    title={label}
                                  >
                                    {label}
                                  </Link>
                                );
                              }

                              // Vessel is not currently in use
                              return '-';
                            })()}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => toggleExpand(vessel.id)}
                                className="text-primary-600 hover:text-primary-700 text-sm"
                                aria-expanded={expandedRows.has(vessel.id)}
                                aria-controls={`vessel-details-${vessel.id}`}
                              >
                                {expandedRows.has(vessel.id) ? 'Hide' : 'Details'}
                              </button>
                              <TableActions>
                                <PermissionButton
                                  resource="vessel"
                                  action="update"
                                  onClick={() => openModal(vessel)}
                                  variant="secondary"
                                  className="!px-2.5 !py-1 !text-sm !bg-transparent !text-monday-text-muted hover:!bg-monday-hover hover:!text-monday-text !border-0 !shadow-none"
                                >
                                  Edit
                                </PermissionButton>
                                {isAdmin && (
                                  <button
                                    onClick={() => handleDelete(vessel.id)}
                                    className="px-2.5 py-1 text-sm font-medium text-monday-text-muted hover:text-error-600 hover:bg-error-50 rounded transition-colors"
                                  >
                                    Delete
                                  </button>
                                )}
                              </TableActions>
                            </div>
                          </td>
                        </tr>
                        {expandedRows.has(vessel.id) && (
                          <tr id={`vessel-details-${vessel.id}`}>
                            <td colSpan={9} className="bg-monday-header px-6 py-4">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-monday-text">
                                <div><span className="font-medium">Location:</span> {vessel.location || '-'}</div>
                                <div><span className="font-medium">Vintage:</span> {vessel.vintage ?? '-'}</div>
                                <div><span className="font-medium">Toast:</span> {vessel.toast || '-'}</div>
                                <div><span className="font-medium">Cooperage:</span> {vessel.cooperage || '-'}</div>
                                <div><span className="font-medium">Age:</span> {vessel.age ?? '-'}</div>
                                <div><span className="font-medium">Manufacture:</span> {vessel.manufacture || '-'}</div>
                                <div><span className="font-medium">Previous Owner:</span> {vessel.previousOwner || '-'}</div>
                                <div><span className="font-medium">Kosher:</span> {vessel.kosher ? 'Yes' : 'No'}</div>
                                <div className="sm:col-span-2 lg:col-span-3">
                                  <span className="font-medium">Notes:</span> {vessel.notes || '-'}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingVessel ? 'Edit Vessel' : 'Add New Vessel'} size="full">
        <div className="mobile-form-container">
          <Formik
            initialValues={vesselToFormValues(editingVessel)}
            validationSchema={VesselSchema}
            onSubmit={(values, formikBag) => {
              handleSubmit(values, formikBag);
            }}
          >
            {({ isSubmitting, values }) => (
              <Form className="space-y-4">
                {formError && (
                  <ErrorMessageComponent error={formError} className="mb-2" />
                )}

                <FormField name="vessel_id" label="Vessel ID" type="text" required />

                <FormSelect
                  name="type"
                  label="Type"
                  options={[
                    { value: '', label: 'Select vessel type' },
                    ...vesselTypes.map(option => ({
                      value: option.value,
                      label: option.label,
                    })),
                  ]}
                  required
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField name="capacity" label="Capacity" type="number" min={0} step="0.01" required />
                  <FormSelect
                    name="capacity_unit"
                    label="Unit"
                    options={capacityUnits.map(option => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField name="age" label="Age" type="number" min={0} step={1} />
                  <FormField name="manufacture" label="Manufacture" type="text" />
                  <FormField name="previous_owner" label="Previous Owner" type="text" />
                </div>

                <FormSelect
                  name="material"
                  label="Material"
                  options={[
                    { value: '', label: 'Select material' },
                    ...vesselMaterials.map(option => ({
                      value: option.value,
                      label: option.label,
                    })),
                  ]}
                  required
                />

                {values.material === 'Other' && (
                  <FormField
                    name="material_other"
                    label="Specify Material"
                    type="text"
                    placeholder="Enter the specific material"
                  />
                )}

                <FormCheckbox name="kosher" label="Certified Kosher" />

                <FormSelect
                  name="usage_type"
                  label="Usage Type"
                  options={usageTypeOptions.map(option => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  required
                />

                <FormSelect
                  name="status"
                  label="Status"
                  options={statusOptions.map(option => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  required
                />

                <FormTextarea name="notes" label="Notes" rows={3} />

                <FormActions onCancel={closeModal} isSubmitting={isSubmitting} submitText="Save" />
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>

      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        onConfirm={confirmDeleteVessel}
        title="Delete Vessel"
        message="Are you sure you want to delete this vessel? This action cannot be undone."
        variant="danger"
      />
    </div>
  );
};

export default Vessels;
